import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";

// --- Core Imports ---
import { useAuth } from "../../contexts/AuthContext"; // Import the useAuth hook

// --- Component Imports ---
import FileInput from "../../components/InputComponent/FileInput";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import axiosInstance from "../../axiosInstance";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

// --- Styles ---
import "./PersonalDetails.scss";

// Helper functions for data transformation
const formatBloodGroupForAPI = (displayValue) => {
  const bloodGroupMap = {
    "A+": "A_POSITIVE",
    "A-": "A_NEGATIVE",
    "A Positive": "A_POSITIVE",
    "A Negative": "A_NEGATIVE",
    "B+": "B_POSITIVE",
    "B-": "B_NEGATIVE",
    "B Positive": "B_POSITIVE",
    "B Negative": "B_NEGATIVE",
    "AB+": "AB_POSITIVE",
    "AB-": "AB_NEGATIVE",
    "AB Positive": "AB_POSITIVE",
    "AB Negative": "AB_NEGATIVE",
    "O+": "O_POSITIVE",
    "O-": "O_NEGATIVE",
    "O Positive": "O_POSITIVE",
    "O Negative": "O_NEGATIVE",
  };
  return bloodGroupMap[displayValue] || displayValue;
};

const formatGenderForAPI = (displayValue) => displayValue?.toUpperCase();
const formatMaritalStatusForAPI = (displayValue) => displayValue?.toUpperCase();

// Validation schemas
const personalDetailsSchema = Yup.object().shape({
  profilePic: Yup.mixed().nullable(),
  emergencyContactName: Yup.string()
    .trim()
    .required("Emergency contact name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters"),
  emergencyContactNo: Yup.string()
    .required("Emergency contact number is required")
    .matches(/^\d{10}$/, "Please enter a valid 10-digit phone number"),
  gender: Yup.string()
    .required("Gender is required")
    .oneOf(["MALE", "FEMALE", "OTHER"], "Please select a valid gender"),
  maritalStatus: Yup.string()
    .required("Marital status is required")
    .oneOf(
      ["SINGLE", "MARRIED", "DIVORCED", "WIDOWED"],
      "Please select a valid marital status"
    ),
  bloodGroup: Yup.string()
    .required("Blood group is required")
    .oneOf(
      [
        "A_POSITIVE",
        "A_NEGATIVE",
        "B_POSITIVE",
        "B_NEGATIVE",
        "AB_POSITIVE",
        "AB_NEGATIVE",
        "O_POSITIVE",
        "O_NEGATIVE",
      ],
      "Please select a valid blood group"
    ),
});

const passwordChangeSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(8, "New password must be at least 8 characters long")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one uppercase, lowercase, number, and special character"
    ),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

const PersonalDetails = () => {
  // Get user data and loading state from AuthContext
  const { user, isLoading: isAuthLoading } = useAuth();

  const [editMode, setEditMode] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [personalDetails, setPersonalDetails] = useState({
    profilePic: null,
    emergencyContactName: "",
    emergencyContactNo: "",
    gender: "",
    maritalStatus: "",
    bloodGroup: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const passwordSectionRef = useRef(null);

  useEffect(() => {
    // Fetch details only when the user object from context is available
    if (user) {
      const fetchPersonalDetails = async () => {
        setIsFetching(true);
        try {
          const response = await axiosInstance.get(
            `${process.env.REACT_APP_API_EMPLOYEE_URL}/employee/profile-Details`
          );
          if (response.data.status === 200) {
            const { data } = response.data;
            // Use the profile image from the context as the primary source of truth
            const profilePicFromContext = user.profileImage
              ? `data:image/jpeg;base64,${user.profileImage}`
              : null;

            setPersonalDetails({
              emergencyContactName: data.emergencyContactName || "",
              emergencyContactNo: String(data.emergencyContactNo || ""),
              gender: formatGenderForAPI(data.gender) || "",
              maritalStatus:
                formatMaritalStatusForAPI(
                  data.martialStatus || data.maritalStatus
                ) || "",
              bloodGroup: formatBloodGroupForAPI(data.bloodGroup) || "",
              profilePic: profilePicFromContext,
            });
          }
        } catch (error) {
          toast.error("Failed to fetch personal details");
        } finally {
          setIsFetching(false);
        }
      };
      fetchPersonalDetails();
    }
  }, [user]); // This effect depends on the user object from the context

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleProfilePicChange = (file, setFieldValue) => {
    if (file) {
      setProfilePicFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setFieldValue("profilePic", reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handlePersonalDetailsSubmit = async (
    values,
    { setSubmitting, setFieldError }
  ) => {
    try {
      const formData = new FormData();
      const details = {
        emergencyContactName: values.emergencyContactName,
        emergencyContactNo: values.emergencyContactNo,
        gender: values.gender,
        maritalStatus: values.maritalStatus,
        bloodGroup: values.bloodGroup,
      };
      formData.append(
        "personalDetails",
        new Blob([JSON.stringify(details)], { type: "application/json" })
      );

      if (profilePicFile) {
        formData.append("ProfileImage", profilePicFile);
      } else {
        formData.append("ProfileImage", new Blob(), "");
      }

      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employee/update`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.status === 200) {
        toast.success("Personal details updated successfully!");
        setEditMode(false);
        setProfilePicFile(null);
        // Reload the page to force AuthContext to refetch the new user data, including the new profile picture.
        window.location.reload();
      }
    } catch (error) {
      toast.error("Failed to update personal details");
      setFieldError("submit", "Something went wrong while submitting.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordChange = async (
    values,
    { setSubmitting, setFieldError, resetForm }
  ) => {
    try {
      if (values.currentPassword === values.newPassword) {
        setFieldError(
          "newPassword",
          "New password must be different from current password"
        );
        return;
      }
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employee/change-password`,
        {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        }
      );
      if (response.status === 200) {
        resetForm();
        setShowPasswordChange(false);
        toast.success("Password changed successfully!");
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setFieldError("currentPassword", "Current password is incorrect");
      } else {
        setFieldError("submit", "Something went wrong while changing password");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (e) => {
    e.preventDefault();
    setEditMode(true);
  };

  const handleCancelClick = (e, resetForm) => {
    e.preventDefault();
    setEditMode(false);
    setProfilePicFile(null);
    resetForm();
  };

  const handlePasswordChangeClick = (e) => {
    e.preventDefault();
    setShowPasswordChange(true);
    setTimeout(() => {
      passwordSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  if (isAuthLoading || isFetching) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <div>Please log in to view your settings.</div>;
  }

  return (
    <div className="profile__form">
      <h2>Personal Settings</h2>
      <p>Update your personal details below.</p>

      <Formik
        initialValues={personalDetails}
        validationSchema={personalDetailsSchema}
        enableReinitialize={true}
        onSubmit={handlePersonalDetailsSubmit}
      >
        {({ errors, touched, isSubmitting, setFieldValue, resetForm }) => (
          <Form className="profile__form">
            <div className="form-group">
              <div className="input-wrapper">
                <label htmlFor="profilePic">Profile Picture</label>
                {editMode ? (
                  <FileInput
                    name="profilePic"
                    label="Choose profile picture"
                    onFileSelect={(file) =>
                      handleProfilePicChange(file, setFieldValue)
                    }
                    accept="image/*"
                  />
                ) : (
                  <div className="profile-pic-display">
                    {personalDetails.profilePic ? (
                      <img
                        src={personalDetails.profilePic}
                        alt="Profile"
                        className="profile-pic-preview"
                      />
                    ) : (
                      <div className="no-profile-pic">No profile picture</div>
                    )}
                  </div>
                )}
              </div>
              <div className="input-wrapper">
                <label htmlFor="emergencyContactName">
                  Emergency Contact Name
                </label>
                <Field
                  id="emergencyContactName"
                  name="emergencyContactName"
                  type="text"
                  disabled={!editMode}
                  className={
                    errors.emergencyContactName && touched.emergencyContactName
                      ? "error"
                      : ""
                  }
                />
                <ErrorMessage
                  name="emergencyContactName"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="input-wrapper">
                <label htmlFor="emergencyContactNo">
                  Emergency Contact Number
                </label>
                <Field
                  id="emergencyContactNo"
                  name="emergencyContactNo"
                  type="text"
                  disabled={!editMode}
                  className={
                    errors.emergencyContactNo && touched.emergencyContactNo
                      ? "error"
                      : ""
                  }
                />
                <ErrorMessage
                  name="emergencyContactNo"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="select-wrapper">
                <label htmlFor="gender">Gender</label>
                <Field
                  as="select"
                  id="gender"
                  name="gender"
                  disabled={!editMode}
                  className={errors.gender && touched.gender ? "error" : ""}
                >
                  <option value="">Select</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </Field>
                <ErrorMessage
                  name="gender"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="select-wrapper">
                <label htmlFor="maritalStatus">Marital Status</label>
                <Field
                  as="select"
                  id="maritalStatus"
                  name="maritalStatus"
                  disabled={!editMode}
                  className={
                    errors.maritalStatus && touched.maritalStatus ? "error" : ""
                  }
                >
                  <option value="">Select</option>
                  <option value="SINGLE">Single</option>
                  <option value="MARRIED">Married</option>
                  <option value="DIVORCED">Divorced</option>
                  <option value="WIDOWED">Widowed</option>
                </Field>
                <ErrorMessage
                  name="maritalStatus"
                  component="div"
                  className="error-message"
                />
              </div>
              <div className="select-wrapper">
                <label htmlFor="bloodGroup">Blood Group</label>
                <Field
                  as="select"
                  id="bloodGroup"
                  name="bloodGroup"
                  disabled={!editMode}
                  className={
                    errors.bloodGroup && touched.bloodGroup ? "error" : ""
                  }
                >
                  <option value="">Select</option>
                  <option value="A_POSITIVE">A+</option>
                  <option value="A_NEGATIVE">A−</option>
                  <option value="B_POSITIVE">B+</option>
                  <option value="B_NEGATIVE">B−</option>
                  <option value="AB_POSITIVE">AB+</option>
                  <option value="AB_NEGATIVE">AB−</option>
                  <option value="O_POSITIVE">O+</option>
                  <option value="O_NEGATIVE">O−</option>
                </Field>
                <ErrorMessage
                  name="bloodGroup"
                  component="div"
                  className="error-message"
                />
              </div>
            </div>
            {errors.submit && (
              <div className="error-message">{errors.submit}</div>
            )}
            <div className="button-container">
              {editMode ? (
                <>
                  <ButtonWithIcon
                    className="submit-btn"
                    icon={"mdi:content-save"}
                    type="submit"
                    text="Save"
                    disabled={isSubmitting}
                  />
                  <ButtonWithIcon
                    className="cancel-btn"
                    icon={"mdi:cancel"}
                    type="button"
                    text="Cancel"
                    onClick={(e) => handleCancelClick(e, resetForm)}
                  />
                </>
              ) : (
                <>
                  <ButtonWithIcon
                    className="edit-btn"
                    icon={"mdi:pencil"}
                    type="button"
                    onClick={handleEditClick}
                    text="Edit"
                  />
                  {!showPasswordChange && (
                    <ButtonWithIcon
                      className="change-password-btn"
                      icon={"mdi:lock"}
                      type="button"
                      onClick={handlePasswordChangeClick}
                      text="Change Password"
                    />
                  )}
                </>
              )}
            </div>
          </Form>
        )}
      </Formik>

      {showPasswordChange && (
        <div
          ref={passwordSectionRef}
          className="password-change-section single-line"
        >
          <Formik
            initialValues={{
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            }}
            validationSchema={passwordChangeSchema}
            onSubmit={handlePasswordChange}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form className="form">
                <div className="password-input-wrapper">
                  <div className="password-input-container">
                    <Field
                      id="currentPassword"
                      name="currentPassword"
                      type={showPasswords.current ? "text" : "password"}
                      placeholder="Current Password"
                      className={
                        errors.currentPassword && touched.currentPassword
                          ? "error"
                          : ""
                      }
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePasswordVisibility("current")}
                    >
                      <Icon
                        icon={showPasswords.current ? "mdi:eye-off" : "mdi:eye"}
                      />
                    </button>
                  </div>
                  <ErrorMessage
                    name="currentPassword"
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="password-input-wrapper">
                  <div className="password-input-container">
                    <Field
                      id="newPassword"
                      name="newPassword"
                      type={showPasswords.new ? "text" : "password"}
                      placeholder="New Password"
                      className={
                        errors.newPassword && touched.newPassword ? "error" : ""
                      }
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePasswordVisibility("new")}
                    >
                      <Icon
                        icon={showPasswords.new ? "mdi:eye-off" : "mdi:eye"}
                      />
                    </button>
                  </div>
                  <ErrorMessage
                    name="newPassword"
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="password-input-wrapper">
                  <div className="password-input-container">
                    <Field
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPasswords.confirm ? "text" : "password"}
                      placeholder="Confirm Password"
                      className={
                        errors.confirmPassword && touched.confirmPassword
                          ? "error"
                          : ""
                      }
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => togglePasswordVisibility("confirm")}
                    >
                      <Icon
                        icon={showPasswords.confirm ? "mdi:eye-off" : "mdi:eye"}
                      />
                    </button>
                  </div>
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="error-message"
                  />
                </div>
                {errors.submit && (
                  <div className="error-message">{errors.submit}</div>
                )}
                <div className="password-button-container">
                  <ButtonWithIcon
                    className="submit-btn"
                    icon={"mdi:content-save"}
                    type="submit"
                    text="Change"
                    disabled={isSubmitting}
                  />
                  <ButtonWithIcon
                    className="cancel-btn"
                    icon={"mdi:cancel"}
                    type="button"
                    text="Cancel"
                    onClick={() => setShowPasswordChange(false)}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default PersonalDetails;
